import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CircularProgress, Step, StepLabel, Stepper } from '@mui/material';
import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';
import importService from '../../utils/apiRequests/importService';
import projectService from '../../utils/apiRequests/projectService';
import { chunkText, mergeExtractions } from '../../utils/import/chunkText';
import {
  ProjectSelectionStep,
  FileUploadStep,
  FormattingStep,
  ChapterDetectionStep,
  AiExtractionStep,
  ReviewStep,
  ImportProgressPanel,
} from './steps';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const BASE_STEPS = ['Formatting', 'Chapter Detection', 'AI Analysis', 'Review'];
const STEPS_WITH_PROJECT_SELECTION = ['Upload File', 'Project', ...BASE_STEPS];
const STEPS_WITHOUT_PROJECT_SELECTION = ['Upload File', ...BASE_STEPS];

const DEFAULT_FORMATTING_OPTIONS = {
  bold: true,
  italic: true,
  underline: true,
  strikethrough: true,
  highlight: true,
  color: true,
  // Off by default: real first-line indents from the document are preserved automatically;
  // this option force-adds a tab to every paragraph and breaks 1:1 round trips.
  tab: false,
};

const DEFAULT_AI_OPTIONS = {
  enabled: true,
  characters: true,
  timeline: true,
  relationships: true,
};

const ImportDocumentModal = ({ open, onClose, projectId, projects, onImportComplete }) => {
  const { colors } = useThemeStyles();

  // Determine if we need project selection step (when not coming from a project view)
  const needsProjectSelection = !projectId;
  const steps = useMemo(
    () => (needsProjectSelection ? STEPS_WITH_PROJECT_SELECTION : STEPS_WITHOUT_PROJECT_SELECTION),
    [needsProjectSelection]
  );

  const [activeStep, setActiveStep] = useState(0);

  // Project selection state
  const [projectMode, setProjectMode] = useState('new'); // 'new' or 'existing'
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectType, setNewProjectType] = useState('novel');

  // File and import state
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [progress, setProgress] = useState(null); // { phase, current, total }

  // Get the effective project ID (either passed in, selected, or created)
  const effectiveProjectId = projectId || createdProjectId || selectedProjectId;

  // Formatting options
  const [formattingOptions, setFormattingOptions] = useState(DEFAULT_FORMATTING_OPTIONS);

  // AI extraction options
  const [aiOptions, setAiOptions] = useState(DEFAULT_AI_OPTIONS);

  // Chapter detection options
  const [chapterPattern, setChapterPattern] = useState('');
  const [usePageBreaks, setUsePageBreaks] = useState(true);
  const [skipTitlePage, setSkipTitlePage] = useState(false);

  const resetState = () => {
    setActiveStep(0);
    setProjectMode('new');
    setSelectedProjectId('');
    setNewProjectTitle('');
    setNewProjectType('novel');
    setCreatedProjectId(null);
    setFile(null);
    setDragOver(false);
    setError('');
    setLoading(false);
    setPreview(null);
    setProgress(null);
    setFormattingOptions(DEFAULT_FORMATTING_OPTIONS);
    setAiOptions(DEFAULT_AI_OPTIONS);
    setChapterPattern('');
    setUsePageBreaks(true);
    setSkipTitlePage(false);
  };

  const handleClose = () => {
    if (loading) return; // Don't close mid-import
    resetState();
    onClose();
  };

  const validateFile = (selectedFile) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    const allowedExtensions = ['.docx', '.doc'];

    const fileName = selectedFile.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));

    if (!allowedTypes.includes(selectedFile.type) && !hasValidExtension) {
      setError('Only Microsoft Word documents (.docx, .doc) are supported.');
      return false;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File size must not exceed 10 MB.');
      return false;
    }

    return true;
  };

  const handleFileSelect = useCallback(
    (selectedFile) => {
      setError('');
      if (validateFile(selectedFile)) {
        setFile(selectedFile);

        // Auto-fill project name with filename (minus extension) if creating new project
        if (needsProjectSelection && projectMode === 'new') {
          const fileNameWithoutExtension = selectedFile.name.replace(/\.[^/.]+$/, '');
          setNewProjectTitle(fileNameWithoutExtension);
        }
      }
    },
    [needsProjectSelection, projectMode] // validateFile is stable, setError and setFile are stable setState functions
  );

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect]
  );

  const handleFormattingChange = (option) => {
    setFormattingOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleAiOptionChange = (option) => {
    setAiOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  const handleNext = async () => {
    // The step before Review fetches the preview
    if (activeStep === steps.length - 2) {
      setLoading(true);
      setError('');

      try {
        const response = await importService.previewDocument(file, {
          chapterPattern,
          usePageBreaks,
          skipTitlePage,
        });

        if (response.error) {
          setError(response.error?.response?.data?.error || 'Failed to preview document');
          setLoading(false);
          return;
        }

        setPreview(response.data);
        setActiveStep((prev) => prev + 1);
      } catch (err) {
        setError('Failed to preview document. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  /**
   * Chunk the manuscript text in the browser and run the AI extraction pass,
   * chunk by chunk, then persist the merged results.
   */
  const runAiExtraction = async (targetProjectId) => {
    const fullText = preview?.fullText || '';
    if (!fullText.trim() || !aiOptions.enabled) {
      return null;
    }
    const wantsAnything = aiOptions.characters || aiOptions.timeline || aiOptions.relationships;
    if (!wantsAnything) {
      return null;
    }

    const chunks = chunkText(fullText);
    if (chunks.length === 0) {
      return null;
    }

    setProgress({ phase: 'analyzing', current: 0, total: chunks.length });

    const results = [];
    let knownCharacters = [];

    for (let i = 0; i < chunks.length; i += 1) {
      // Chunks are analyzed sequentially so known characters accumulate and
      // the analysis service isn't overwhelmed.
      // eslint-disable-next-line no-await-in-loop
      const response = await importService.analyzeChunk(targetProjectId, {
        text: chunks[i],
        extract: {
          characters: aiOptions.characters,
          timeline: aiOptions.timeline,
          relationships: aiOptions.relationships,
        },
        knownCharacters,
      });

      if (!response.error && response.data) {
        results.push(response.data);
        knownCharacters = mergeExtractions(results).characters.map((c) => c.fullName);
      }

      setProgress({ phase: 'analyzing', current: i + 1, total: chunks.length });
    }

    if (results.length === 0) {
      return null;
    }

    const merged = mergeExtractions(results);
    setProgress({ phase: 'saving', current: chunks.length, total: chunks.length });
    return importService.applyExtractions(targetProjectId, merged);
  };

  const handleImport = async () => {
    setLoading(true);
    setError('');

    try {
      let targetProjectId = effectiveProjectId;

      // If creating a new project, create it first
      if (needsProjectSelection && projectMode === 'new' && !createdProjectId) {
        setProgress({ phase: 'creating' });
        const projectTitle =
          newProjectTitle.trim() || file?.name?.replace(/\.[^/.]+$/, '') || 'Imported Document';
        const projectResponse = await projectService.createProject({
          title: projectTitle,
          type: newProjectType,
        });

        if (projectResponse.error) {
          const errorMsg =
            projectResponse.error?.response?.data?.error || 'Failed to create project';
          setError(errorMsg);
          setLoading(false);
          setProgress(null);
          return;
        }

        targetProjectId = projectResponse.data?.project?._id;
        if (!targetProjectId) {
          setError('Failed to create project. Please try again.');
          setLoading(false);
          setProgress(null);
          return;
        }
        setCreatedProjectId(targetProjectId);
      }

      // Import the document into the project.
      // This modal runs its own chunked AI extraction pass below, so the
      // server-side extraction is disabled to avoid duplicate characters/events.
      setProgress({ phase: 'importing' });
      const response = await importService.importDocument(targetProjectId, file, {
        ...formattingOptions,
        chapterPattern,
        usePageBreaks,
        skipTitlePage,
        importCharacters: false,
        importTimeline: false,
      });

      if (response.error) {
        setError(response.error?.response?.data?.error || 'Failed to import document');
        setLoading(false);
        setProgress(null);
        return;
      }

      // Run the AI extraction pass (chunked, client-driven)
      let extractionSummary = null;
      try {
        const extractionResponse = await runAiExtraction(targetProjectId);
        if (extractionResponse && !extractionResponse.error) {
          extractionSummary = extractionResponse.data;
        }
      } catch (extractionError) {
        // Extraction is best-effort; the manuscript import already succeeded.
        console.warn('AI extraction failed:', extractionError);
      }

      if (onImportComplete) {
        onImportComplete(
          { ...response.data, extraction: extractionSummary },
          projectMode === 'new'
        );
      }
      setProgress(null);
      handleClose();
    } catch (err) {
      setError('Failed to import document. Please try again.');
      setProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    if (loading && progress) {
      return (
        <ImportProgressPanel
          phase={progress.phase}
          current={progress.current || 0}
          total={progress.total || 0}
        />
      );
    }

    const currentStepLabel = steps[activeStep];

    switch (currentStepLabel) {
      case 'Project':
        return (
          <ProjectSelectionStep
            projectMode={projectMode}
            onProjectModeChange={(mode) => {
              setProjectMode(mode);
              if (mode === 'new') {
                setSelectedProjectId('');
              }
            }}
            newProjectTitle={newProjectTitle}
            onNewProjectTitleChange={setNewProjectTitle}
            newProjectType={newProjectType}
            onNewProjectTypeChange={setNewProjectType}
            projects={projects}
            selectedProjectId={selectedProjectId}
            onSelectProject={setSelectedProjectId}
          />
        );
      case 'Upload File':
        return (
          <FileUploadStep
            file={file}
            dragOver={dragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onFileChange={handleFileChange}
            onRemoveFile={() => setFile(null)}
          />
        );
      case 'Formatting':
        return (
          <FormattingStep formattingOptions={formattingOptions} onToggle={handleFormattingChange} />
        );
      case 'Chapter Detection':
        return (
          <ChapterDetectionStep
            usePageBreaks={usePageBreaks}
            onUsePageBreaksChange={setUsePageBreaks}
            skipTitlePage={skipTitlePage}
            onSkipTitlePageChange={setSkipTitlePage}
            chapterPattern={chapterPattern}
            onChapterPatternChange={(value) => {
              setChapterPattern(value);
              if (value) {
                setUsePageBreaks(false);
              }
            }}
          />
        );
      case 'AI Analysis':
        return <AiExtractionStep aiOptions={aiOptions} onChange={handleAiOptionChange} />;
      case 'Review':
        return (
          <ReviewStep
            preview={preview}
            formattingOptions={formattingOptions}
            aiOptions={aiOptions}
          />
        );
      default:
        return null;
    }
  };

  const canProceed = () => {
    const currentStepLabel = steps[activeStep];

    switch (currentStepLabel) {
      case 'Project':
        // For new project mode, always can proceed (title is optional, will use file name)
        // For existing project mode, must have selected a project
        if (projectMode === 'new') {
          return true;
        }
        return selectedProjectId !== '';
      case 'Upload File':
        return file !== null;
      case 'Review':
        return preview !== null;
      default:
        return true;
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Import Document"
      width={560}
      ariaLabelledby="import-document-modal-title"
      ariaDescribedby="import-document-modal-description"
      showCloseButton
      contentSx={{ gap: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {/* Fixed Header: Stepper and Error */}
      <Box sx={{ flexShrink: 0 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.border.subtle,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: colors.border.primary,
            },
          },
        }}
      >
        <Box sx={{ minHeight: 280 }}>{renderStepContent()}</Box>
      </Box>

      {/* Fixed Footer: Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${colors.border.subtle}`,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
          variant="outlined"
          sx={{
            color: colors.text.secondary,
            borderColor: colors.border.subtle,
            '&:hover': {
              color: colors.text.primary,
              borderColor: colors.border.primary,
            },
          }}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{
              color: colors.text.secondary,
              borderColor: colors.border.subtle,
              '&:hover': {
                color: colors.text.primary,
                borderColor: colors.border.primary,
              },
            }}
          >
            Cancel
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={!canProceed() || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Next'}
            </Button>
          ) : (
            <Button
              onClick={handleImport}
              variant="contained"
              color="primary"
              disabled={!canProceed() || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Import'}
            </Button>
          )}
        </Box>
      </Box>
    </ReusableModal>
  );
};

ImportDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      title: PropTypes.string,
      type: PropTypes.string,
      thinChapters: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
  onImportComplete: PropTypes.func,
};

ImportDocumentModal.defaultProps = {
  projectId: null,
  projects: [],
  onImportComplete: null,
};

export default ImportDocumentModal;
